import React, { useEffect, useRef, useState } from 'react';
import { setSessionStorageItem } from '../../utils/browserStorage';

const desktopLinkClass =
  'rounded-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-400 focus-visible:ring-offset-2 focus-visible:ring-offset-white';
const mobileLinkClass =
  'block rounded-md px-3 py-2 font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white';

const navLinks = [
  { href: '#home', label: 'Home' },
  { href: '#portfolio', label: 'Portfolio' },
  { href: '#services', label: 'Services' },
  { href: '#faq', label: 'FAQ' },
  { href: '#about', label: 'About' },
  { href: '#contact', label: 'Contact' }
];

export const Navigation = ({ scrollY, isMenuOpen, setIsMenuOpen }) => {
  const [activeSection, setActiveSection] = useState('home');
  const closeMobileMenu = () => setIsMenuOpen(false);
  const navRef = useRef(null);
  const menuToggleButtonRef = useRef(null);
  const previouslyFocusedElementRef = useRef(null);

  useEffect(() => {
    if (!isMenuOpen || typeof document === 'undefined') {
      return undefined;
    }

    previouslyFocusedElementRef.current = document.activeElement;

    const previousOverflow = document.body.style.overflow;
    const menuToggleButton = menuToggleButtonRef.current;
    document.body.style.overflow = 'hidden';

    const focusableSelector =
      '#mobile-navigation-menu a[href], #mobile-navigation-menu button:not([disabled]), #mobile-navigation-menu [tabindex]:not([tabindex="-1"])';

    const getFocusableElements = () =>
      Array.from(document.querySelectorAll(focusableSelector)).filter(
        (element) => !element.hasAttribute('disabled')
      );

    const focusableElements = getFocusableElements();

    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
        return;
      }

      if (event.key === 'Tab') {
        const currentFocusableElements = getFocusableElements();

        if (currentFocusableElements.length === 0) {
          return;
        }

        const firstElement = currentFocusableElements[0];
        const lastElement = currentFocusableElements[currentFocusableElements.length - 1];

        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
          return;
        }

        if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    };

    const handlePointerDown = (event) => {
      if (!navRef.current || navRef.current.contains(event.target)) {
        return;
      }

      setIsMenuOpen(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    document.addEventListener('pointerdown', handlePointerDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('pointerdown', handlePointerDown);

      if (
        previouslyFocusedElementRef.current &&
        typeof previouslyFocusedElementRef.current.focus === 'function'
      ) {
        previouslyFocusedElementRef.current.focus();
      } else if (menuToggleButton && typeof menuToggleButton.focus === 'function') {
        menuToggleButton.focus();
      }
    };
  }, [isMenuOpen, setIsMenuOpen]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMenuOpen(false);
      }
    };

    const handleHashChange = () => {
      setIsMenuOpen(false);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [setIsMenuOpen]);

  useEffect(() => {
    if (typeof document === 'undefined' || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    const sectionIds = navLinks.map((item) => item.href.replace('#', ''));
    const sections = sectionIds
      .map((id) => document.getElementById(id))
      .filter(Boolean);

    if (sections.length === 0) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisibleEntry = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        if (mostVisibleEntry?.target?.id) {
          setActiveSection(mostVisibleEntry.target.id);
        }
      },
      {
        root: null,
        rootMargin: '-30% 0px -55% 0px',
        threshold: [0.1, 0.25, 0.5, 0.75]
      }
    );

    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  const getDesktopLinkClass = (href) => {
    const isActive = activeSection === href.replace('#', '');
    return `${desktopLinkClass} ${isActive ? 'text-teal-700' : 'text-gray-600 hover:text-teal-600'}`;
  };

  const getMobileLinkClass = (href) => {
    const isActive = activeSection === href.replace('#', '');
    return `${mobileLinkClass} ${
      isActive ? 'bg-teal-50 text-teal-700' : 'text-gray-600 hover:bg-teal-50 hover:text-teal-600'
    }`;
  };

  const handleGenericAvailabilityClick = () => {
    if (typeof window !== 'undefined') {
      setSessionStorageItem('hero_focus_contact_form', '1');
      window.dispatchEvent(new CustomEvent('hero:availability-click'));
    }
  };

  return React.createElement(
    'nav',
    {
      className: `fixed top-0 w-full z-50 transition-all duration-300 ${
        scrollY > 50 ? 'bg-white/90 backdrop-blur-md shadow-lg border-b border-teal-100' : 'bg-white/70 backdrop-blur-sm'
      }`,
      'aria-label': 'Primary',
      ref: navRef
    },
    React.createElement(
      'div',
      {
        className: 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'
      },
      React.createElement(
        'div',
        {
          className: 'flex justify-between items-center h-16'
        },
        React.createElement(
          'a',
          {
            href: '#home',
            className:
              'flex min-w-0 items-center space-x-2 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-400 focus-visible:ring-offset-2 focus-visible:ring-offset-white'
          },
          React.createElement('img', {
            src: '/images/logo.svg',
            alt: 'Cassidy Sullivan Photography Logo',
            width: 48,
            height: 48,
            className: 'h-11 w-11 shrink-0 sm:h-12 sm:w-12'
          }),
          React.createElement(
            'span',
            {
              className: 'truncate text-base font-bold text-gray-800 tracking-wide sm:text-xl',
              'aria-label': 'Cassidy Sullivan Photography'
            },
            React.createElement('span', { className: 'sm:hidden' }, 'Cassidy Sullivan'),
            React.createElement('span', { className: 'hidden sm:inline' }, 'Cassidy Sullivan Photography')
          )
        ),

        React.createElement(
          'div',
          {
            className: 'hidden md:flex items-center space-x-8'
          },
          ...navLinks.map((item) =>
            React.createElement(
              'a',
              {
                key: item.href,
                href: item.href,
                className: getDesktopLinkClass(item.href),
                'aria-current': activeSection === item.href.replace('#', '') ? 'page' : undefined
              },
              item.label
            )
          ),
          React.createElement(
            'a',
            {
              href: 'tel:+16069226307',
              className:
                'inline-flex items-center rounded-full border border-teal-200 bg-white px-4 py-2 text-sm font-semibold text-teal-700 shadow-sm transition-colors hover:bg-teal-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-300 focus-visible:ring-offset-2 focus-visible:ring-offset-white'
            },
            'Call Cassidy'
          ),
          React.createElement(
            'a',
            {
              href: '#contact-form',
              onClick: handleGenericAvailabilityClick,
              className:
                'inline-flex items-center rounded-full bg-gradient-to-r from-teal-500 to-cyan-600 px-5 py-2 text-sm font-semibold text-white shadow-md transition-all hover:from-teal-600 hover:to-cyan-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-300 focus-visible:ring-offset-2 focus-visible:ring-offset-white'
            },
            'Check Availability'
          )
        ),

        React.createElement(
          'button',
          {
            onClick: () => setIsMenuOpen(!isMenuOpen),
            className:
              'md:hidden rounded-md p-1 text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white',
            'aria-label': isMenuOpen ? 'Close menu' : 'Open menu',
            'aria-expanded': isMenuOpen,
            'aria-controls': 'mobile-navigation-menu',
            ref: menuToggleButtonRef
          },
          isMenuOpen
            ? React.createElement(
                'svg',
                {
                  className: 'h-6 w-6',
                  viewBox: '0 0 24 24',
                  fill: 'none',
                  stroke: 'currentColor'
                },
                React.createElement('line', { x1: '18', y1: '6', x2: '6', y2: '18' }),
                React.createElement('line', { x1: '6', y1: '6', x2: '18', y2: '18' })
              )
            : React.createElement(
                'svg',
                {
                  className: 'h-6 w-6',
                  viewBox: '0 0 24 24',
                  fill: 'none',
                  stroke: 'currentColor'
                },
                React.createElement('line', { x1: '3', y1: '6', x2: '21', y2: '6' }),
                React.createElement('line', { x1: '3', y1: '12', x2: '21', y2: '12' }),
                React.createElement('line', { x1: '3', y1: '18', x2: '21', y2: '18' })
              )
        )
      )
    ),

    isMenuOpen &&
      React.createElement(
        'div',
        {
          id: 'mobile-navigation-menu',
          className: 'md:hidden bg-white/95 backdrop-blur-md border-t border-teal-100',
          role: 'dialog',
          'aria-modal': 'true',
          'aria-label': 'Mobile navigation menu'
        },
        React.createElement(
          'div',
          {
            className: 'px-2 pt-2 pb-3 space-y-1'
          },
          ...navLinks.map((item) =>
            React.createElement(
              'a',
              {
                key: item.href,
                href: item.href,
                className: getMobileLinkClass(item.href),
                onClick: closeMobileMenu,
                'aria-current': activeSection === item.href.replace('#', '') ? 'page' : undefined
              },
              item.label
            )
          ),
          React.createElement(
            'a',
            {
              href: 'tel:+16069226307',
              onClick: closeMobileMenu,
              className:
                'mx-3 mt-2 block rounded-full border border-teal-200 bg-white px-4 py-3 text-center font-semibold text-teal-700 shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white'
            },
            'Call Cassidy'
          ),
          React.createElement(
            'a',
            {
              href: '#contact-form',
              onClick: () => {
                handleGenericAvailabilityClick();
                closeMobileMenu();
              },
              className:
                'mx-3 mt-2 block rounded-full bg-gradient-to-r from-teal-500 to-cyan-600 px-4 py-3 text-center font-semibold text-white shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white'
            },
            'Check Availability'
          )
        )
      )
  );
};
